import { Logger } from '~~/helpers/logger';
import { useUserApi } from '~/utils/ct/composables/api/useUserApi';
import { useCart } from '~/utils/ct/composables/useCart';
import type { Customer, CustomQuery, UseUserErrors, UseUserLoginParams, UseUserRegisterParams } from '~~/types/commercetools';
import { errorToString } from '~~/helpers/error/errorToString';

import { maskEmail } from '~~/helpers/maskEmail';

export function useUser() {

  const {
    updateUser: apiUpdateUser,
    register: apiRegister,
    login: apiLogin,
    logout: apiLogout,
    changePassword: apiChangePassword,
    load: apiLoad,
  } = useUserApi();

  const { setCart } = useCart();

  const errorsFactory = (): UseUserErrors => ({
    updateUser: null,
    register: null,
    login: null,
    logout: null,
    changePassword: null,
    load: null,
  });
  const user: Ref<Customer | null> = useState('useUser-user', () => null);
  const loading = useState('useUser-loading', () => false);
  const isAuthenticated = computed(() => Boolean(user.value));
  const error: Ref<UseUserErrors> = useState('useUser-error', () => errorsFactory());

  function setUser(newUser: Customer) {

    user.value = newUser;
    Logger.debug('useUserFactory.setUser', newUser);
  
  }

  function resetErrorValue() {

    error.value = errorsFactory();
  
  }

  function handleError (errorKey: keyof UseUserErrors, err: unknown) {

    const caughtError = err instanceof Error ? err : new Error(errorToString(err));
    error.value[errorKey] = caughtError;
    Logger.error(`useUser/${errorKey}`, caughtError);
  
  }

  async function updateUser(
    { providedUser, customQuery }:
    { providedUser: Partial<Customer>, customQuery?: CustomQuery },
  ) {

    Logger.debug('useUserFactory.updateUser', providedUser);
    resetErrorValue();
    try {

      loading.value = true;

      if (!user.value) {

        throw new Error('User is not authenticated');
      
      }
      const currentUser = user.value;
      const response = await apiUpdateUser({
        currentUser,
        updatedUserData: providedUser,
        customQuery,
      });

      if (response?.user) {

        user.value = response.user;
      
      }
    
    } catch (err) {

      handleError('updateUser', err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  async function register({ providedUser }: { providedUser: UseUserRegisterParams }) {

    Logger.debug('useUserFactory.register', providedUser);
    resetErrorValue();
    try {

      loading.value = true;
      const response = await apiRegister({ ...providedUser }, setCart);
      user.value = response;
      error.value.register = null;
    
    } catch (err) {

      handleError('register', err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  async function login({ user: providedUser }: { user: UseUserLoginParams }) {

    Logger.debug('useUserFactory.login', providedUser);
    resetErrorValue();
    try {

      loading.value = true;
      const response = await apiLogin({ ...providedUser }, setCart);
      user.value = response;
      error.value.login = null;

    } catch (err) {

      handleError('login', err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  async function logout() {

    Logger.debug('useUserFactory.logout');
    resetErrorValue();
    try {

      await apiLogout(setCart);
      error.value.logout = null;
      user.value = null;
    
    } catch (err) {

      handleError('logout', err);
    
    }
  
  }

  async function changePassword(params: {
    current: string,
    new: string,
    customQuery?: CustomQuery,
  }) {

    Logger.debug('useUserFactory.changePassword', {
      currentPassword: maskEmail(params.current),
      newPassword: maskEmail(params.new),
    });
    resetErrorValue();
    try {

      loading.value = true;

      if (!user.value) {

        throw new Error('User is not authenticated');
      
      }
      const currentUser = user.value;
      const response = await apiChangePassword({
        currentUser,
        currentPassword: params.current,
        newPassword: params.new,
        customQuery: params.customQuery,
      }, setCart);
      user.value = response;
      error.value.changePassword = null;
    
    } catch (err) {

      handleError('changePassword', err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  async function load({ customQuery } = { customQuery: undefined }) {

    Logger.debug('useUserFactory.load');
    resetErrorValue();
    try {

      loading.value = true;
      const response = await apiLoad({ customQuery });
      user.value = response;
      error.value.load = null;
    
    } catch (err) {

      handleError('load', err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  return {
    user,
    setUser,
    isAuthenticated,
    load,
    logout,
    login,
    updateUser,
    register,
    changePassword,
    loading,
    error,
  };

}
